/**
 * Vista Hiring Plan (Agente TA Metrics V1.3, doc v5 §15): la misma tabla que
 * TA filtra a mano en la planilla, dentro de internal y de solo lectura
 * (RD-10). Filtros combinables (RD-02), contador (RD-03), estado Abierta por
 * defecto con Stand By/Request/Pipeline/Programada a un clic (RD-04), orden
 * por días abierta / apertura / etapa (RD-05), detalle con el embudo (RD-06),
 * comparación contra una foto (RD-09). Las filas vienen ya normalizadas del
 * backend (`taMetrics.listRows`, RD-07): acá solo se filtra y se ordena.
 */
import { useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, ArrowUpDown, RefreshCw, X } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelectPopover, type MultiSelectOption } from '@/components/ui/multi-select-popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useCompareTaSnapshots, useTaHiringPlanRows, useTaSnapshots } from '@/hooks/useTalentAcquisition';
import { usePagination } from '@/hooks/usePagination';
import { cn, formatDate, formatDateTime } from '@/lib/utils';
import { canon, diffDays, ETAPAS_CANDIDATOS, nivelMasAvanzado, type TaEstado } from '@/../../shared/ta-metrics';
import { TaEstadoBadge } from './TaEstadoBadge';
import { HiringPlanDetailDialog, type HiringPlanRow } from './HiringPlanDetailDialog';
import { TaSnapshotDiffPanel } from './TaSnapshotDiffPanel';

/** Estados que se suman al Abierta por defecto con un clic (RD-04). */
const ESTADOS_RAPIDOS: TaEstado[] = ['Stand By', 'Request', 'Pipeline', 'Programada'];
const ESTADOS: TaEstado[] = ['Abierta', ...ESTADOS_RAPIDOS, 'Cerrada', 'Cancelada', 'Otro'];

type CampoMulti = 'Area' | 'Team' | 'recruiter' | 'pais' | 'tipo' | 'Lead';
const CAMPOS_MULTI: CampoMulti[] = ['Area', 'Team', 'recruiter', 'pais', 'tipo', 'Lead'];

type SortKey = 'dias_abierta' | 'fecha_apertura' | 'etapa' | 'posicion' | 'estado';
type Sort = { key: SortKey; dir: 'asc' | 'desc' };

const ETAPA_MAS_AVANZADA = Object.fromEntries(Object.entries(ETAPAS_CANDIDATOS).map(([e, n]) => [n, e])) as Record<number, string>;

interface Filtros {
  estado: TaEstado[];
  multi: Record<CampoMulti, string[]>;
  seniority: string[];
  posicion: string;
}
const FILTROS_INICIALES: Filtros = { estado: ['Abierta'], multi: { Area: [], Team: [], recruiter: [], pais: [], tipo: [], Lead: [] }, seniority: [], posicion: '' };

/** Nivel canónico de seniority, o el literal si no está en el diccionario. */
const nivelDe = (r: HiringPlanRow) => r.seniority_nivel ?? r.Seniority ?? '';

/**
 * Forma canónica memoizada (la planilla escribe "Studio"/"Studio "/"México"/"Mexico"
 * como valores distintos): la tabla filtra y agrupa igual que `filtrar` del bot
 * (RD-07), así el contador coincide con el bot y con el panel de comparación.
 */
const canonCache = new Map<string, string>();
const canonDe = (v: string) => { let c = canonCache.get(v); if (c === undefined) { c = canon(v); canonCache.set(v, c); } return c; };

export function TaHiringPlanTab() {
  const { t } = useTranslation();
  const k = 'talent_acquisition.hiring_plan';
  const rows = useTaHiringPlanRows();
  const fotos = useTaSnapshots();
  const [filtros, setFiltros] = useState<Filtros>(FILTROS_INICIALES);
  const [sort, setSort] = useState<Sort>({ key: 'etapa', dir: 'desc' });
  const [detalle, setDetalle] = useState<HiringPlanRow | null>(null);
  const [fotoDesde, setFotoDesde] = useState<string>('');

  const hoy = rows.data?.hoy ?? '';
  const todas = useMemo(() => (rows.data?.rows ?? []) as HiringPlanRow[], [rows.data]);

  // Filtrado client-side por forma canónica (como `filtrar` del bot); las opciones salen de la misma base
  const filtradas = useMemo(() => {
    const needle = filtros.posicion.trim().toLowerCase();
    const selCanon = Object.fromEntries(CAMPOS_MULTI.map((c) => [c, filtros.multi[c].map(canonDe)])) as Record<CampoMulti, string[]>;
    const senCanon = filtros.seniority.map(canonDe);
    return todas.filter((r) => {
      if (filtros.estado.length && !filtros.estado.includes(r.estado)) return false;
      for (const c of CAMPOS_MULTI) {
        const sel = selCanon[c];
        if (sel.length && !sel.includes(canonDe(String(r[c] ?? '')))) return false;
      }
      if (senCanon.length && !senCanon.includes(canonDe(nivelDe(r)))) return false;
      if (needle && !`${r.posicion} ${r.Position} ${r.ID}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [todas, filtros]);

  const diasAbierta = (r: HiringPlanRow) => (r.fecha_apertura ? diffDays(r.fecha_apertura, hoy) : null);
  const ordenadas = useMemo(() => {
    const mul = sort.dir === 'asc' ? 1 : -1;
    const num = (v: number | null) => (v === null ? -Infinity : v);
    return [...filtradas].sort((a, b) => {
      switch (sort.key) {
        case 'dias_abierta': return (num(diasAbierta(a)) - num(diasAbierta(b))) * mul;
        case 'fecha_apertura': return (a.fecha_apertura ?? '').localeCompare(b.fecha_apertura ?? '') * mul;
        case 'etapa': return (nivelMasAvanzado(a) - nivelMasAvanzado(b)) * mul || num(diasAbierta(b)) - num(diasAbierta(a));
        case 'posicion': return a.posicion.localeCompare(b.posicion) * mul;
        case 'estado': return a.estado.localeCompare(b.estado) * mul;
      }
    });
  }, [filtradas, sort, hoy]); // eslint-disable-line react-hooks/exhaustive-deps
  const { pageItems: paginaActual, footer: paginationFooter } = usePagination(ordenadas, 25);

  // Opciones de cada filtro con conteo, calculadas sobre las filas que pasan los DEMÁS filtros.
  // Se agrupan por forma canónica ("Studio" y "Studio " son una sola opción, con el primer literal como etiqueta).
  const opciones = useMemo(() => {
    const selCanon = Object.fromEntries(CAMPOS_MULTI.map((c) => [c, filtros.multi[c].map(canonDe)])) as Record<CampoMulti, string[]>;
    const senCanon = filtros.seniority.map(canonDe);
    const calc = (campo: CampoMulti | 'estado' | 'seniority'): MultiSelectOption[] => {
      const m = new Map<string, { value: string; count: number }>();
      for (const r of todas) {
        if (campo !== 'estado' && filtros.estado.length && !filtros.estado.includes(r.estado)) continue;
        let ok = true;
        for (const c of CAMPOS_MULTI) { if (c !== campo && selCanon[c].length && !selCanon[c].includes(canonDe(String(r[c] ?? '')))) { ok = false; break; } }
        if (!ok) continue;
        if (campo !== 'seniority' && senCanon.length && !senCanon.includes(canonDe(nivelDe(r)))) continue;
        const v = campo === 'estado' ? r.estado : campo === 'seniority' ? nivelDe(r) : String(r[campo] ?? '').trim();
        if (!v) continue;
        const key = campo === 'estado' ? v : canonDe(v);
        const cur = m.get(key);
        if (cur) cur.count += 1; else m.set(key, { value: v, count: 1 });
      }
      if (campo === 'estado') return ESTADOS.map((e) => ({ value: e, count: m.get(e)?.count ?? 0 }));
      return [...m.values()].sort((a, b) => b.count - a.count || a.value.localeCompare(b.value));
    };
    const out = {} as Record<CampoMulti | 'estado' | 'seniority', MultiSelectOption[]>;
    for (const c of [...CAMPOS_MULTI, 'estado', 'seniority'] as const) out[c] = calc(c);
    return out;
  }, [todas, filtros]);

  const setMulti = (c: CampoMulti, next: string[]) => setFiltros((f) => ({ ...f, multi: { ...f.multi, [c]: next } }));
  const toggleEstado = (e: TaEstado) => setFiltros((f) => ({ ...f, estado: f.estado.includes(e) ? f.estado.filter((x) => x !== e) : [...f.estado, e] }));
  const hayFiltros = filtros.posicion || filtros.seniority.length || CAMPOS_MULTI.some((c) => filtros.multi[c].length) || filtros.estado.join() !== 'Abierta';
  const toggleSort = (key: SortKey) => setSort((s) => (s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: key === 'posicion' || key === 'estado' ? 'asc' : 'desc' }));

  // RD-09: comparación contra una foto, con los mismos filtros de la vista
  const filtrosCompare = useMemo(() => ({
    estado: filtros.estado.length ? filtros.estado : undefined,
    ...Object.fromEntries(CAMPOS_MULTI.filter((c) => filtros.multi[c].length).map((c) => [c, filtros.multi[c]])),
    ...(filtros.seniority.length ? { Seniority: filtros.seniority } : {}),
  }), [filtros]);
  const compare = useCompareTaSnapshots({ desde: fotoDesde || undefined, filtros: filtrosCompare }, !!fotoDesde);

  const SortHead = ({ sortKey, label, className }: { sortKey: SortKey; label: string; className?: string }) => (
    <TableHead className={className}>
      <button type="button" className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => toggleSort(sortKey)} aria-label={t(`${k}.sort_by`, { column: label })}>
        {label}
        {sort.key === sortKey ? (sort.dir === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </TableHead>
  );

  if (rows.isLoading) return <div className="flex items-center justify-center py-24"><WinclapLoader size={40} /></div>;
  if (rows.isError || !rows.data) {
    return (
      <Alert variant="destructive">
        <AlertTitle>{t('talent_acquisition.load_error_title')}</AlertTitle>
        <AlertDescription className="flex flex-wrap items-center gap-3">
          <span>{t('talent_acquisition.load_error_desc')} {rows.error?.message}</span>
          <Button size="sm" variant="outline" className="gap-1.5" onClick={() => rows.refetch()}><RefreshCw className="h-3.5 w-3.5" /> {t('talent_acquisition.rules.retry')}</Button>
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">{t(`${k}.desc`)}</p>

      {/* Estado: Abierta por defecto + los estados previos a un clic (RD-04) */}
      <div className="flex flex-wrap items-center gap-1.5">
        {ESTADOS.map((e) => {
          const on = filtros.estado.includes(e);
          const n = opciones.estado.find((o) => o.value === e)?.count ?? 0;
          return (
            <button
              key={e}
              type="button"
              aria-pressed={on}
              onClick={() => toggleEstado(e)}
              className={cn('rounded-full border px-2.5 py-0.5 text-xs transition-colors', on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-accent')}
            >
              {e} <span className={cn('tabular-nums', on ? 'opacity-80' : 'text-muted-foreground')}>{n}</span>
            </button>
          );
        })}
      </div>

      {/* Filtros combinables (RD-02) */}
      <div className="flex flex-wrap items-center gap-2">
        {CAMPOS_MULTI.map((c) => (
          <MultiSelectPopover key={c} label={t(`${k}.filters.${c}`)} options={opciones[c]} selected={filtros.multi[c]} onChange={(n) => setMulti(c, n)} searchPlaceholder={t(`${k}.filters.search`)} allLabel={t(`${k}.filters.all`)} />
        ))}
        <MultiSelectPopover label={t(`${k}.filters.seniority`)} options={opciones.seniority} selected={filtros.seniority} onChange={(n) => setFiltros((f) => ({ ...f, seniority: n }))} searchPlaceholder={t(`${k}.filters.search`)} allLabel={t(`${k}.filters.all`)} />
        <Input value={filtros.posicion} onChange={(e) => setFiltros((f) => ({ ...f, posicion: e.target.value }))} placeholder={t(`${k}.filters.posicion`)} className="h-8 w-56 text-sm" aria-label={t(`${k}.filters.posicion`)} />
        {hayFiltros && (
          <Button type="button" variant="ghost" size="sm" className="h-8 gap-1 text-xs" onClick={() => setFiltros(FILTROS_INICIALES)}>
            <X className="h-3 w-3" /> {t(`${k}.filters.clear`)}
          </Button>
        )}
      </div>

      {/* Contador (RD-03) + comparación contra una foto (RD-09) */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm">
          <Trans i18nKey={`${k}.counter`} count={filtradas.length} components={{ strong: <strong className="tabular-nums" /> }} />
          <span className="text-muted-foreground"> · {t(`${k}.en_proceso_total`, { n: filtradas.reduce((n, r) => n + r.candidatos.length, 0) })}</span>
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{t(`${k}.compare.label`)}</span>
          <Select value={fotoDesde || '__none__'} onValueChange={(v) => setFotoDesde(v === '__none__' ? '' : v)}>
            <SelectTrigger className="h-8 w-52 text-xs" aria-label={t(`${k}.compare.label`)}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">{t(`${k}.compare.none`)}</SelectItem>
              {(fotos.data ?? []).map((f) => <SelectItem key={f.fecha} value={f.fecha}>{t(`${k}.compare.foto`, { fecha: f.fecha, n: f.busquedas })}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      {fotoDesde && (
        <TaSnapshotDiffPanel diff={compare.data?.diff} nota={compare.data?.nota} loading={compare.isLoading} error={compare.error?.message ?? null} />
      )}

      {/* La tabla (RD-01) */}
      <div className="overflow-x-auto rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">ID</TableHead>
              <SortHead sortKey="posicion" label={t(`${k}.columns.posicion`)} />
              <TableHead>{t(`${k}.columns.area_team`)}</TableHead>
              <SortHead sortKey="estado" label={t(`${k}.columns.estado`)} />
              <TableHead>OL</TableHead>
              <SortHead sortKey="etapa" label={t(`${k}.columns.etapa`)} />
              <TableHead className="text-right">{t(`${k}.columns.en_proceso`)}</TableHead>
              <TableHead className="text-right">{t(`${k}.columns.contactados`)}</TableHead>
              <TableHead>{t(`${k}.columns.recruiter`)}</TableHead>
              <TableHead>{t(`${k}.columns.hm`)}</TableHead>
              <TableHead>{t(`${k}.columns.pais`)}</TableHead>
              <TableHead>{t(`${k}.columns.seniority`)}</TableHead>
              <SortHead sortKey="fecha_apertura" label={t(`${k}.columns.apertura`)} />
              <SortHead sortKey="dias_abierta" label={t(`${k}.columns.dias`)} className="text-right" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordenadas.length === 0 && (
              <TableRow><TableCell colSpan={14} className="py-10 text-center text-sm text-muted-foreground">{t(`${k}.empty`)}</TableCell></TableRow>
            )}
            {paginaActual.map((r, i) => {
              const nivel = nivelMasAvanzado(r);
              const dias = diasAbierta(r);
              return (
                <TableRow
                  key={`${r.ID}-${i}`}
                  className="cursor-pointer focus-visible:bg-accent focus-visible:outline-hidden"
                  role="button"
                  tabIndex={0}
                  aria-label={t(`${k}.open_detail`, { posicion: r.posicion || r.Position })}
                  onClick={() => setDetalle(r)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetalle(r); } }}
                >
                  <TableCell className="text-xs tabular-nums text-muted-foreground">{r.ID}</TableCell>
                  <TableCell className="max-w-[260px] truncate font-medium" title={r.posicion}>{r.posicion || r.Position}</TableCell>
                  <TableCell className="max-w-[200px] truncate text-xs" title={`${r.Area} · ${r.Team}`}>{r.Area}{r.Team ? <span className="text-muted-foreground"> · {r.Team}</span> : null}</TableCell>
                  <TableCell><TaEstadoBadge estado={r.estado} /></TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{r.OL}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{nivel > 0 ? ETAPA_MAS_AVANZADA[nivel] : <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.candidatos.length}</TableCell>
                  <TableCell className="text-right tabular-nums">{r.contactados ?? '—'}</TableCell>
                  <TableCell className="text-xs">{r.recruiter}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-xs" title={r.Lead}>{r.Lead}</TableCell>
                  <TableCell className="text-xs">{r.pais}</TableCell>
                  <TableCell className="text-xs">{r.Seniority}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{r.fecha_apertura ? formatDate(r.fecha_apertura) : '—'}</TableCell>
                  <TableCell className={cn('text-right tabular-nums', dias !== null && dias > 60 && r.estado === 'Abierta' && 'font-semibold text-amber-700 dark:text-amber-300')}>{dias ?? '—'}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {paginationFooter}
      <p className="text-xs text-muted-foreground">{t(`${k}.footer`, { rows: rows.data.rows.length, at: rows.data.downloadedAt ? formatDateTime(rows.data.downloadedAt) : '', today: hoy })}</p>

      <HiringPlanDetailDialog row={detalle} hoy={hoy} onClose={() => setDetalle(null)} />
    </div>
  );
}
